import type { Duration } from 'luxon'

import {
  SESSION_FEEDBACK_MAX_DAYS_BACK,
  SESSION_FEEDBACK_NUM_MINUTES_EARLY,
} from '@/utils/constants'
import type {
  CertificationProfileAttributes,
  CourseAttributes,
  LearnerFeedbackFeaturedAttributes,
  PostFeedbackAttributes,
  PostFeedbackFeaturedAttributes,
  SeriesAttributes,
  SessionAttributes,
  SessionFeedbackFeaturedAttributes,
  SessionRegistrationAttributes,
  TopicAttributes,
  TutorAuditAttributes,
  TutorAuditFeaturedAttributes,
  TutorProfileAttributes,
  UserAttributes,
  UserVolunteerRoleAttributes,
  VolunteerTeamAttributes,
  ZipCodeInfoAttributes,
} from '@/server/models'
import type { LearnerFeedbackAttributes } from '@/server/models/LearnerFeedback'

import ConversationQ from '@/queries/ConversationQ'
import ConversationUserQ from '@/queries/ConversationUserQ'
import {
  type CertificationStatus,
  DELETED_USER_BIO,
  DELETED_USER_FIRST_NAME,
  DELETED_USER_LAST_NAME,
  LIVE_HELP_TOPIC,
  type MigratedDialoguesData,
  NEW_TUTOR_ONBOARDING_TOPIC,
  type ProgressionLevel,
  type ResearchGroup,
  type SATPilotState,
  SESSION_FEEDBACK_TYPE_TUTOR,
  SessionAttendeeRole,
  type SessionMentorAction,
  TARGET_USER_INCOME_THRESHOLD,
  TopicType,
  TutorSessionFeedbackRating,
  type UserInternalState,
  type VolunteerRoleSlug,
} from '@/queries/constants'
import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import VolunteerActivityQ from '@/queries/VolunteerActivityQ'
import VolunteerRoleApplicationSubmissionQ from '@/queries/VolunteerRoleApplicationSubmissionQ'
import { SQL, type SQLFragment } from '@/client/query'
import type { EducationInputValue } from '@/client/services/formInputs/onboarding'
import {
  EDUCATION_INPUT_NAME,
  EDUCATION_K_12_JOIN_REASON_INPUT_NAME,
  EDUCATION_K_12_SCHOOL_EXPERIENCE_INPUT_NAME,
  EDUCATION_NOT_K_12_JOIN_REASON_INPUT_NAME,
  JoinReason,
  type SchoolExperience,
} from '@/client/services/formInputs/onboarding/constants'
import type { LearnerFeedbackType } from '@/client/services/learnerFeedback/types'
import {
  DETECTED_ISSUE_TYPE_TO_URGENCY,
  type DetectedIssueType,
  type DetectedIssueUrgency,
  type ModerationFlagSeverity,
} from '@/client/services/moderation'
import type { PrimaryInterestV2 } from '@/client/services/primaryInterest/types'
import type { ProgramIteration } from '@/client/services/programs/types'
import type { SATInvitationParams } from '@/client/services/satBootcamp'
import { AUDITOR_SESSIONS_HOSTED_MIN_THRESHOLD } from '@/client/services/sessionAudit/constants'
import type { TutorAuditorLevel } from '@/client/services/sessionAudit/types'
import { OLD_AUDITOR_TRAINING_SLUG, PEER_REVIEW_TRAINING_SLUG } from '@/client/services/training'
import { SUGGEST_NEW_TUTOR_ONBOARDING_UNTIL_NUM_SESSIONS_HOSTED } from '@/client/services/tutorOnboarding'
import { SUSPENDED_ROLES } from '@/client/services/user'
import type { UserAuthRole } from '@/client/services/userRole/types'
import type { PostFeedbackRating } from '@/client/services/worlds'
import {
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  SAT_BOOTCAMP_WORLD_SLUG,
} from '@/client/services/worlds/constants'

import AssessmentSubmissionQ from './AssessmentSubmissionQ'
import BackgroundCheckQ from './BackgroundCheckQ'
import CertificationProfileQ from './CertificationProfileQ'
import CertificationRequestQ from './CertificationRequestQ'
import ContentFlagQ from './ContentFlagQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import DetectedIssueQ from './DetectedIssueQ'
import { filterAttendingLiveHelpSessions } from './helpers'
import KeyClubClubQ from './KeyClubClubQ'
import LearnerFeedbackFeaturedQ from './LearnerFeedbackFeaturedQ'
import MessageQ from './MessageQ'
import PlatformFeedbackQ from './PlatformFeedbackQ'
import PostFeedbackFeaturedQ from './PostFeedbackFeaturedQ'
import PostFeedbackQ from './PostFeedbackQ'
import ReferralSourceQ from './ReferralSourceQ'
import SATUserQ from './SATUserQ'
import SeriesHostQ from './SeriesHostQ'
import SeriesQ from './SeriesQ'
import SeriesRegistrationQ from './SeriesRegistrationQ'
import SeriesReservationQ from './SeriesReservationQ'
import SeriesTagQ from './SeriesTagQ'
import SessionAttendeeQ from './SessionAttendeeQ'
import SessionFeedbackFeaturedQ from './SessionFeedbackFeaturedQ'
import SessionFeedbackQ from './SessionFeedbackQ'
import SessionHostQ from './SessionHostQ'
import SessionQ from './SessionQ'
import SessionRegistrationQ from './SessionRegistrationQ'
import TopicFollowQ from './TopicFollowQ'
import TopicQ from './TopicQ'
import TrainingQ from './TrainingQ'
import TrainingSubmissionQ from './TrainingSubmissionQ'
import TutorAuditFeaturedQ from './TutorAuditFeaturedQ'
import TutorAuditQ from './TutorAuditQ'
import TutorFollowQ from './TutorFollowQ'
import TutorProfileQ from './TutorProfileQ'
import TutorQuizQ from './TutorQuizQ'
import UserEventQ from './UserEventQ'
import UserRoleQ from './UserRoleQ'
import UserVolunteerRoleQ from './UserVolunteerRoleQ'
import VolunteerRoleQ from './VolunteerRoleQ'
import VolunteerTeamQ from './VolunteerTeamQ'
import WebsiteSettingsQ from './WebsiteSettingsQ'
import WorldMemberQ from './WorldMemberQ'
import WorldQ from './WorldQ'
import ZipCodeInfoQ from './ZipCodeInfoQ'

const ROLE_FOUNDING_TUTOR: UserAuthRole = 'user:founding_tutor'
const ROLE_MENTOR: UserAuthRole = 'admin:mentor'
const ROLE_TUTOR: UserAuthRole = 'user:tutor'
const ROLE_SUPER_ADMIN: UserAuthRole = 'admin:super'
const ROLE_APPROVED: UserAuthRole = 'user:approved'
const ROLE_BLOCKED: UserAuthRole = 'user:blocked'
const ROLE_REJECTED: UserAuthRole = 'user:rejected'
const ROLE_BLOCKED_COMMUNITY: UserAuthRole = 'user:blocked:community'
const ROLE_RESEARCH_OPT_OUT: UserAuthRole = 'user:recording:opt_out'

// Update whenever terms change substantially.
export const LATEST_TUTOR_TERMS = 'accepted_tutor_terms_07_19_21'
export const LATEST_COMMUNITY_GUIDELINES = 'accepted_community_guidelines_12_13_21'

const SESSION_END_DATE_BUFFER_DAYS = 1

export const ONE_ON_ONE_SESSIONS_ATTENDED_QUOTA = 5

const UserQ = {
  ...makeQueryModelV2<UserAttributes>('user')([
    'id',
    'pronouns',
    'email',
    'phone_number',
    'username',
    'date_of_birth',
    'avatar',
    'timezone',
    'country',
    'languages',
    'referral_name',
    'onboarding_details',
    'uuid_transcript',
    'referral_source_id',
    'reviewing_user_id',
    'suspension_actions',
    'progression',
    'key_club_club_id',
    'college_info',
    'college_info_verified_at',
    'college_info_visible',
    'created_at',
    'updated_at',
    'deleted_at',
    'last_online_at',
    'username_updated_at',
    'notifications_last_viewed_at',
    'personal_data_deleted_at',
    'application_screen_details',
    'unread_messages_email_snooze_until',
    'phone_number_waived_at',
    'phone_number_verified_at',
    'portfolio_share_intentions',
    'parent_consent_requested_at',
    'parent_consent_completed_at',
    'parent_consent_waived_at',
    'parent_details',
    'assent_completed_at',
    'age_assurance_history',

    'virtual_avatar_url',
    'virtual_display_name',
    'virtual_progression_points',
  ]),

  first_name: SQL<string>`
    CASE WHEN "user"."deleted_at" IS NOT NULL THEN
      ${DELETED_USER_FIRST_NAME}
    ELSE
      "user"."first_name"
    END
  `,
  last_name: SQL<string>`
    CASE WHEN "user"."deleted_at" IS NOT NULL THEN
      ${DELETED_USER_LAST_NAME}
    ELSE
      "user"."last_name"
    END
  `,
  display_name: SQL<string>`"user"."virtual_display_name"`,
  bio: SQL<string>`
    CASE WHEN "user"."deleted_at" IS NOT NULL THEN
      ${DELETED_USER_BIO}
    ELSE
      "user"."bio"
    END
  `,
  avatar_url: SQL<string>`"user"."virtual_avatar_url"`,
  points: SQL<number>`"user"."virtual_progression_points"`,
  progression_level: SQL<ProgressionLevel>`get_user_progression_level("user")`,
  member_since: SQL<string>`get_member_since("user")`,
  marketing_source: SQL<string>`"user"."internal_state"->>'marketing_source'`,
  primary_interest: SQL<PrimaryInterestV2>`"user"."internal_state"->'primary_interest_v2'`,

  parent_first_name: SQL<string>`"user"."parent_details"->>'firstName'`,
  parent_last_name: SQL<string>`"user"."parent_details"->>'lastName'`,
  parent_email: SQL<string>`"user"."parent_details"->>'email'`,
  parent_phone: SQL<string>`"user"."parent_details"->>'phone'`,

  // We archive the user's personal data in internal_state, so this
  // is a safety mechanism to prevent us from accidentally exposing it.
  internal_state: SQL<UserInternalState>`
    CASE WHEN "user"."deleted_at" IS NOT NULL THEN
      '{}'::JSONB
    ELSE
      "user"."internal_state"
    END
  `,

  roles: SQL<UserAuthRole[]>`
    (
      SELECT COALESCE(ARRAY_AGG(${UserRoleQ.role}), '{}')::TEXT[] AS "roles"
      ${UserRoleQ.where({ userId: SQL`"user"."id"` })}
    )
  `,

  associations: (): AssociationDefinitions => ({
    TutorProfile: {
      query: TutorProfileQ,
      foreignKey: TutorProfileQ.user_id,
      sourceKey: UserQ.id,
    },
    SATUsers: {
      query: SATUserQ,
      foreignKey: SATUserQ.user_id,
      sourceKey: UserQ.id,
    },
    KeyClubClub: {
      query: KeyClubClubQ,
      foreignKey: KeyClubClubQ.id,
      sourceKey: UserQ.key_club_club_id,
    },
    ReferralSource: {
      query: ReferralSourceQ,
      foreignKey: ReferralSourceQ.id,
      sourceKey: UserQ.referral_source_id,
    },
    UserRole: {
      query: UserRoleQ,
      foreignKey: UserRoleQ.user_id,
      sourceKey: UserQ.id,
    },
    WorldMember: {
      query: WorldMemberQ,
      foreignKey: WorldMemberQ.user_id,
      sourceKey: UserQ.id,
    },
    BackgroundCheck: {
      query: BackgroundCheckQ,
      foreignKey: BackgroundCheckQ.user_id,
      sourceKey: UserQ.id,
    },
  }),

  where: (
    {
      id,
      email,
      username,
      uuidTranscript,
      referralSourceId,
      paranoid = true,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | SQLFragment<number> | null
      email?: string | SQLFragment<string> | null
      username?: string | SQLFragment<string> | null
      uuidTranscript?: string | SQLFragment<string> | null
      referralSourceId?: string | SQLFragment<string> | null
      paranoid?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<UserAttributes> => SQL`
        FROM ${UserQ}
        ${includeAssociationsToSql(UserQ, include)}
        WHERE
            ${id != null ? SQL`${UserQ.id} = ${id} AND` : SQL``}
            ${email != null ? SQL`${UserQ.email} = ${email} AND` : SQL``}
            ${username != null ? SQL`${UserQ.username} = ${username} AND` : SQL``}
            ${
              referralSourceId != null
                ? SQL`${UserQ.referral_source_id} = ${referralSourceId} AND`
                : SQL``
            }
            ${
              uuidTranscript != null ? SQL`${UserQ.uuid_transcript} = ${uuidTranscript} AND` : SQL``
            }
            ${paranoid ? SQL`${UserQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${limit != null ? SQL`LIMIT ${limit}` : SQL``}
        `,

  getCollegeInfoCountryCode: (): SQLFragment<string> => SQL`${UserQ.college_info}->>'countryCode'`,

  hasCollegeInfoCountryCode: (countryCode: string): SQLFragment<boolean> =>
    SQL`(${UserQ.getCollegeInfoCountryCode()} = ${countryCode})`,

  getFilteredCollegeInfo: (): SQLFragment<{ name: string; major: string }> => SQL`
    (CASE WHEN ${UserQ.college_info} IS NULL OR ${UserQ.college_info_verified_at} IS NULL OR ${UserQ.college_info_visible} = FALSE
        THEN NULL
      ELSE JSON_BUILD_OBJECT('name', ${UserQ.college_info}->>'name', 'major', ${UserQ.college_info}->>'major')
    END)`,

  countReferredUsersByUserId: (userId: number | SQLFragment<number>): SQLFragment<number> =>
    SQL`
      COUNT(*)
      FROM ${UserQ} u2
      WHERE u2.referral_source_id IN (
        SELECT ${ReferralSourceQ.id}
        ${ReferralSourceQ.where({ userId })}
      )
      AND EXISTS (
        SELECT 1
        ${UserRoleQ.where({
          userId: SQL`"u2".id`,
          role: 'user:approved',
        })}
      )
    `,

  countReferredTutorsByUserId: (userId: number | SQLFragment<number>): SQLFragment<number> =>
    SQL`
      COUNT(*)
      FROM ${UserQ} u2
      WHERE u2.referral_source_id IN (
        SELECT ${ReferralSourceQ.id} ${ReferralSourceQ.where({ userId })}
      )
      AND 'user:tutor' IN (
        SELECT "role"
        ${UserRoleQ.where({
          userId: SQL`"u2".id`,
        })}
      )
    `,

  countSessionFeedback: (): SQLFragment<number> =>
    SQL`COUNT(*) FROM ${SessionFeedbackQ} WHERE ${SessionFeedbackQ.session_id}
            IN (SELECT ${SessionQ.id} ${SessionQ.where({ tutor: UserQ.id })})`,

  countSessionsThatRequireFeedbackOrAudit: (): SQLFragment<number> =>
    SQL`COUNT(*) ${UserQ.getSessionsThatRequireFeedbackOrAudit()}`,

  // TODO: This query starts at SessionRegistration. This is wrong, start it on the session instead
  countPastRegisteredSessions: (): SQLFragment<number> =>
    SQL`COUNT(${SessionRegistrationQ}.*) FROM ${SessionRegistrationQ} INNER JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionRegistrationQ.session_id}
                AND ${SessionQ.end_date} < CURRENT_TIMESTAMP
                WHERE ${SessionRegistrationQ.user_id} = ${UserQ.id}
                AND ${SessionRegistrationQ.session_breakout_id} IS NULL
                AND ${SessionRegistrationQ.deleted_at} IS NULL`,

  // TODO: This query starts at SessionRegistration. This is wrong, start it on the session instead
  countUpcomingAttendingSessions: (): SQLFragment<number> =>
    SQL`COUNT(${SessionRegistrationQ}.*) FROM ${SessionRegistrationQ} INNER JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionRegistrationQ.session_id}
                AND ${SessionQ.end_date} > CURRENT_TIMESTAMP
                WHERE ${SessionRegistrationQ.user_id} = ${UserQ.id}
                AND ${SessionRegistrationQ.session_breakout_id} IS NULL
                AND ${SessionRegistrationQ.deleted_at} IS NULL`,

  getLastSessionAttended: (): SQLFragment<number> =>
    SQL`FROM ${SessionQ} JOIN ${SessionRegistrationQ}
                ON ${SessionRegistrationQ.session_id} = ${SessionQ.id}
                WHERE ${SessionRegistrationQ.user_id} = ${UserQ.id}
                AND ${SessionQ.start_date} < NOW()
                AND (SELECT ${SessionRegistrationQ.getZoomAttendanceSeconds()}) > 0
            ORDER BY ${SessionQ.start_date} DESC LIMIT 1`,

  // TODO: This query starts at SessionRegistration. This is wrong, start it on the session instead
  countSessionsAttended: (): SQLFragment<number> => SQL`
    COUNT(${SessionRegistrationQ}.*) FROM ${SessionRegistrationQ} INNER JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionRegistrationQ.session_id}
                AND ${SessionQ.end_date} <= CURRENT_TIMESTAMP
                WHERE ${SessionRegistrationQ.user_id} = ${UserQ.id}
                AND ${SessionRegistrationQ.attempted_joining}
                AND ${SessionRegistrationQ.session_breakout_id} IS NULL
                AND ${SessionRegistrationQ.deleted_at} IS NULL`,

  countPastHostedSessions: ({
    requireLearnerAttendance,
    customWhere,
  }: {
    requireLearnerAttendance: boolean
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<number> =>
    SQL`COUNT(*) ${UserQ.getPastHostedSessions({ requireLearnerAttendance, customWhere })}`,

  countPastHostedSessionsIncludingMigrated: ({
    requireLearnerAttendance,
    customWhere,
  }: {
    requireLearnerAttendance: boolean
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<number> =>
    SQL`(SELECT ${UserQ.countPastHostedSessions({ requireLearnerAttendance, customWhere })}) + GREATEST(0, (${
      UserQ.internal_state
    }->>'migrated_num_sessions')::int)`,

  getPastHostedSessions: ({
    requireLearnerAttendance,
    customWhere,
  }: {
    requireLearnerAttendance: boolean
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<SessionAttributes> =>
    SessionQ.where(
      {
        include: [
          { association: 'Topic', required: true },
          {
            association: 'Series',
            required: false,
          },
        ],
      },
      SQL`${SessionQ.id} IN (SELECT ${SessionQ.id} ${UserQ.getTutoringSessions({
        userId: UserQ.id,
        requireLearnerAttendance,
        customWhere: SessionQ.hasEnded(),
      })})
      AND ${customWhere != null ? customWhere : SQL`TRUE`}`
    ),

  getSeriesWithPastHostedSessions: ({
    requireLearnerAttendance,
    seriesTemplateSlugs,
  }: {
    requireLearnerAttendance: boolean
    seriesTemplateSlugs?: string[]
  }): SQLFragment<SessionAttributes> =>
    SeriesQ.where(
      {
        include: [{ association: 'Course', required: true }],
      },
      SQL`${SeriesQ.hasPastSessionHostedBy({
        userId: UserQ.id,
        requireLearnerAttendance,
      })}
      AND ${seriesTemplateSlugs != null ? SeriesQ.hasAnyTemplateSlug(seriesTemplateSlugs) : SQL`TRUE`}`
    ),

  getPastParticipatedDialoguesSessions: ({
    customWhere,
  }: {
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<SessionAttributes> =>
    SessionQ.where(
      {},
      SQL`EXISTS(SELECT 1 FROM ${SessionRegistrationQ}
        WHERE ${SessionRegistrationQ.session_id} = ${SessionQ.id}
          AND ${SessionRegistrationQ.user_id} = ${UserQ.id}
          AND ${SessionRegistrationQ.attempted_joining} IS TRUE
          AND ${SessionRegistrationQ.session_breakout_id} IS NOT NULL
      )
      AND ${SessionQ.isDialoguesSeriesSession()}
      AND ${customWhere != null ? customWhere : SQL`TRUE`}`
    ),

  getPastParticipatedDialoguesSeries: (): SQLFragment<SeriesAttributes[]> =>
    SeriesQ.where(
      {
        include: [{ association: 'Course', required: true }],
      },
      SQL`
      EXISTS(SELECT 1 ${UserQ.getPastParticipatedDialoguesSessions({
        customWhere: SQL`${SessionQ.series_id} = ${SeriesQ.id} AND ${SessionQ.hasEnded()}`,
      })})`
    ),

  getAttendedSessions: ({
    userId,
    customWhere = null,
  }: {
    userId: number | SQLFragment<number>
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<SessionAttributes> =>
    SessionQ.where(
      {},
      SQL`${SessionQ.didUserAttemptJoining(userId)} AND ${
        customWhere != null ? customWhere : SQL`TRUE`
      }`
    ),

  countAttendedSessions: ({
    userId,
    customWhere = null,
  }: {
    userId: number | SQLFragment<number>
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<number> => SQL`COUNT(*) ${UserQ.getAttendedSessions({ userId, customWhere })}`,

  countUpcomingSessionsHostedByUser: (): SQLFragment<number> =>
    SQL`COUNT(*) ${SessionQ.where({ tutor: UserQ.id })} AND
                ${SessionQ.end_date} > CURRENT_TIMESTAMP`,

  getPostsWithFeedback: (): SQLFragment<PostFeedbackAttributes> =>
    SQL`${PostFeedbackQ.where(
      {
        userId: UserQ.id,
        include: [{ association: 'Post', required: true }],
      },
      SQL`${PostFeedbackQ.rating} IS NOT NULL`
    )}`,

  countPostsWithFeedback: (): SQLFragment<number> => SQL`COUNT(*) ${UserQ.getPostsWithFeedback()}`,

  getFirstPendingFriendReservedSeries: (): SQLFragment<SeriesAttributes> =>
    SeriesReservationQ.where(
      {
        userId: UserQ.id,
        include: [
          {
            association: 'Series',
            required: true,
          },
        ],
        order: [[SeriesReservationQ.created_at, 'asc']],
        limit: 1,
      },
      SQL`
        (${SeriesReservationQ.reserver_user_id} != ${UserQ.id})
        AND NOT ${SeriesQ.isUserRegisteredOrSelfReserved(UserQ.id)}
      `
    ),

  countFollowers: (): SQLFragment<number> =>
    SQL`COUNT(*) FROM ${TutorFollowQ} WHERE ${TutorFollowQ.tutor_id} = ${UserQ.id}`,

  getSATInvitationParams: (): SQLFragment<SATInvitationParams | null> =>
    SQL`${UserQ.internal_state}->'sat_invitation_params'`,

  getCollegeBoardState: (): SQLFragment<SATPilotState> =>
    SQL`${UserQ.internal_state}->'sat_pilot_v2'`,

  hasCollegeBoardChildWithType: (type: 'learner' | 'tutor'): SQLFragment<boolean> => SQL`
      EXISTS(SELECT 1 FROM jsonb_array_elements(${UserQ.getCollegeBoardState()}->'children') e WHERE e->>'type' = ${type})`,

  getCollegeBoardId: (): SQLFragment<number> =>
    SQL`${UserQ.getCollegeBoardState()}->>'college_board_id'`,

  isCollegeBoard: (): SQLFragment<boolean> => SQL`${UserQ.getCollegeBoardState()} IS NOT NULL`,

  isCollegeBoardPilotLearner: (): SQLFragment<number> =>
    SQL`${UserQ.getCollegeBoardPilotId()} IS NOT NULL AND ${UserQ.getCollegeBoardPilotId()} > 100000 AND ${UserQ.getCollegeBoardPilotId()} < 1000000`,

  getCollegeBoardPilotId: (): SQLFragment<number> =>
    SQL`CAST(${UserQ.internal_state}->>'college_board_pilot_id' AS INT)`,

  getTutoringSessions: ({
    userId,
    requireLearnerAttendance,
    customWhere = null,
  }: {
    userId: number | SQLFragment<number>
    requireLearnerAttendance: boolean
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<SessionAttributes> => SQL`
    FROM (
      SELECT ${SessionQ}.*
      ${SessionHostQ.where(
        {
          userId,
          approved: true,
          attemptedHosting: true,
          include: [
            {
              association: 'Session',
              required: true,
              where: requireLearnerAttendance ? SessionQ.hasAtLeastOneLearner() : null,
            },
          ],
        },
        customWhere
      )}
    ) AS ${SessionQ}
  `,

  isUnreviewed: (): SQLFragment<UserAttributes> =>
    SQL`(${UserQ.deleted_at} IS NULL
        AND ${UserQ.onboarding_details} IS NOT NULL
        AND NOT (${UserQ.hasBeenReviewed()}))`,

  countSessionRatings: ({
    rating,
    dateFilterMin = null,
    dateFilterMax = null,
  }: {
    rating: TutorSessionFeedbackRating
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<number> =>
    SQL`
      COUNT(*)
      ${SessionFeedbackQ.where(
        { rating, type: SESSION_FEEDBACK_TYPE_TUTOR },
        SQL`
          ${SessionFeedbackQ.session_id} IN (SELECT ${SessionQ.id} ${UserQ.getTutoringSessions({
            requireLearnerAttendance: false,
            userId: UserQ.id,
          })}) AND
          ${
            dateFilterMin == null
              ? SQL`TRUE`
              : SQL`${SessionFeedbackQ.created_at} > ${dateFilterMin}`
          }
          AND ${
            dateFilterMax == null
              ? SQL`TRUE`
              : SQL`${SessionFeedbackQ.created_at} < ${dateFilterMax}`
          }
        `
      )}`,

  countPostRatings: ({
    rating,
    dateFilterMin,
    dateFilterMax,
  }: {
    rating: PostFeedbackRating
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<number> =>
    SQL`
    COUNT(*)
    ${PostFeedbackQ.where(
      {
        rating,
        userId: UserQ.id,
      },
      SQL`${dateFilterMin == null ? SQL`TRUE` : SQL`${PostFeedbackQ.created_at} > ${dateFilterMin}`}
        AND ${
          dateFilterMax == null ? SQL`TRUE` : SQL`${PostFeedbackQ.created_at} < ${dateFilterMax}`
        }`
    )}`,

  countPositiveRatings: ({
    dateFilterMin,
    dateFilterMax,
  }: {
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<number> =>
    SQL`
    (SELECT ${UserQ.countSessionRatings({
      rating: TutorSessionFeedbackRating.Helpful,
      dateFilterMin,
      dateFilterMax,
    })}) +
    (SELECT ${UserQ.countSessionRatings({
      rating: TutorSessionFeedbackRating.SuperHelpful,
      dateFilterMin,
      dateFilterMax,
    })}) +
    (SELECT ${UserQ.countPostRatings({ rating: 'Helpful', dateFilterMin, dateFilterMax })}) +
    (SELECT ${UserQ.countPostRatings({ rating: 'Super Helpful', dateFilterMin, dateFilterMax })})
    `,

  getRatingTags: ({ limit }: { limit?: number }): SQLFragment<{ count: number; tag: string }[]> =>
    SQL`
      FROM (
        SELECT
          COUNT(${SessionFeedbackQ.id}) as "count",
          UNNEST(${SessionFeedbackQ.rating_tags}) as "tag"
        ${SessionFeedbackQ.where(
          { type: SESSION_FEEDBACK_TYPE_TUTOR },
          SQL`${SessionFeedbackQ.session_id} IN (SELECT ${SessionQ.id} ${UserQ.getTutoringSessions({
            userId: UserQ.id,
            requireLearnerAttendance: false,
          })})`
        )}
        GROUP BY tag
        ORDER BY "count" DESC
        ${queryLimitToSQL(limit ?? null)}
      ) as "rating_tags"
      `,

  // Like countRatings() but includes migrated count
  countHelpfulSessionRatingsIncludingMigrated: (): SQLFragment<number> =>
    SQL`
    (SELECT ${UserQ.countSessionRatings({ rating: TutorSessionFeedbackRating.Helpful })})
    + GREATEST(0, (${UserQ.internal_state}->>'migrated_num_helpful')::int)
    `,

  // Like countRatings() but includes migrated count
  countSuperHelpfulSessionRatingsIncludingMigrated: (): SQLFragment<number> =>
    SQL`
    (SELECT ${UserQ.countSessionRatings({ rating: TutorSessionFeedbackRating.SuperHelpful })})
    + GREATEST(0, (${UserQ.internal_state}->>'migrated_num_super_helpful')::int)
    `,

  countAllHelpfuls: (): SQLFragment<number> =>
    SQL`
      ${UserQ.countHelpfulSessionRatingsIncludingMigrated()}
      + (SELECT ${UserQ.countPostRatings({ rating: 'Helpful' })})
    `,

  countAllSuperHelpfuls: (): SQLFragment<number> =>
    SQL`
      ${UserQ.countSuperHelpfulSessionRatingsIncludingMigrated()}
      + (SELECT ${UserQ.countPostRatings({ rating: 'Super Helpful' })})
    `,

  countAllNotHelpfuls: (): SQLFragment<number> =>
    SQL`
      (SELECT ${UserQ.countSessionRatings({ rating: TutorSessionFeedbackRating.NotHelpful })})
      + (SELECT ${UserQ.countPostRatings({ rating: 'Not Helpful' })})
    `,

  getSessionsThatRequireFeedbackOrAudit: (): SQLFragment<SessionAttributes> =>
    SQL`${SessionQ.where(
      { include: [{ association: 'Topic', required: true }] },
      SQL`
        NOT (${TopicQ.name} = ${LIVE_HELP_TOPIC} AND ${SessionQ.world_id} IS NOT NULL)
        AND ${SessionQ.id} IN (
          SELECT ${SessionRegistrationQ.session_id} ${SessionRegistrationQ.where({
            userId: UserQ.id,
          })}
          AND (${SessionRegistrationQ.snooze_feedback_until} IS NULL OR
          ${SessionRegistrationQ.snooze_feedback_until} < CURRENT_TIMESTAMP)
        )
        AND EXISTS(SELECT 1 ${SessionAttendeeQ.where({
          userId: UserQ.id,
          sessionId: SessionQ.id,
        })})
        AND NOT(EXISTS(SELECT 1 FROM ${SessionFeedbackQ} WHERE ${SessionFeedbackQ.user_id} = ${UserQ.id}
        AND ${SessionFeedbackQ.session_id} = ${SessionQ.id}))
        AND NOT(EXISTS(SELECT 1 ${TutorAuditQ.where({
          auditorId: UserQ.id,
          sessionId: SessionQ.id,
        })}))
        AND ${SessionQ.getComputedEndDate()} < CURRENT_TIMESTAMP + (INTERVAL '1 minute' * ${SESSION_FEEDBACK_NUM_MINUTES_EARLY})
        AND ${SessionQ.getComputedEndDate()} > NOW() - (INTERVAL '1 DAY' * ${SESSION_FEEDBACK_MAX_DAYS_BACK})
        AND ${SessionQ.deleted_at} IS NULL`
    )}`,

  getFirstSessionThatRequiresFeedbackOrAudit: (): SQLFragment<SessionAttributes> =>
    SQL`${UserQ.getSessionsThatRequireFeedbackOrAudit()}
        ORDER BY ${SessionQ.start_date} ASC
        LIMIT 1`,

  hasSomeRole: (roles: UserAuthRole[] = []): SQLFragment<boolean> =>
    SQL`(${UserQ.roles}::TEXT[] && ${roles})`,

  hasRole: (role: UserAuthRole): SQLFragment<boolean> => UserQ.hasSomeRole([role]),

  hasTutorRole: (): SQLFragment<boolean> => UserQ.hasRole(ROLE_TUTOR),

  hasSuperAdminRole: (): SQLFragment<boolean> => UserQ.hasRole(ROLE_SUPER_ADMIN),

  hasFoundingTutorRole: (): SQLFragment<boolean> => UserQ.hasRole(ROLE_FOUNDING_TUTOR),

  hasMentorRole: (): SQLFragment<boolean> => UserQ.hasRole(ROLE_MENTOR),

  hasOptedOutOfResearch: (): SQLFragment<boolean> => UserQ.hasRole(ROLE_RESEARCH_OPT_OUT),

  hasBeenApproved: (): SQLFragment<boolean> => UserQ.hasRole(ROLE_APPROVED),

  hasBeenSuspended: (): SQLFragment<boolean> => UserQ.hasSomeRole(SUSPENDED_ROLES),

  hasBeenReviewed: (): SQLFragment<boolean> =>
    UserQ.hasSomeRole([ROLE_APPROVED, ROLE_REJECTED, ROLE_BLOCKED, ROLE_BLOCKED_COMMUNITY]),

  hasAcceptedLatestCommunityGuidelines: (): SQLFragment<boolean> =>
    SQL`(${UserQ.internal_state}->>${LATEST_COMMUNITY_GUIDELINES})::boolean`,

  hasAcceptedLatestTutorTerms: (): SQLFragment<boolean> =>
    SQL`(${UserQ.internal_state}->>${LATEST_TUTOR_TERMS})::boolean`,

  getTutorQuizAttempts: (): SQLFragment<number> =>
    SQL`COUNT(*) ${TutorQuizQ.where({ userId: UserQ.id })}`,

  getTutorQuiz: ({
    approved,
  }: {
    approved?: boolean
  } = {}): SQLFragment<TutorProfileAttributes> => SQL`
        ${TutorQuizQ.where({
          userId: UserQ.id,
          approved,
        })}
        ORDER BY ${TutorQuizQ.approved_at} ASC
        LIMIT 1
    `,

  getTutorQuizStatus: (): SQLFragment<CertificationStatus> => SQL`
        GREATEST(
            0,
            (SELECT
                CASE WHEN
                    ${TutorQuizQ.approved_at} IS NULL
                    THEN 1
                    ELSE 2
                END
                ${UserQ.getTutorQuiz()}
            )
        )
    `,

  getTutorProfile: ({
    approved,
  }: {
    approved?: boolean
  } = {}): SQLFragment<TutorProfileAttributes> => SQL`
        ${TutorProfileQ.where({
          userId: UserQ.id,
          approved,
        })}
        ORDER BY ${TutorProfileQ.approved_at} ASC
        LIMIT 1
    `,

  getTutorProfileStatus: (): SQLFragment<CertificationStatus> => SQL`
        GREATEST(
            0,
            (SELECT
                CASE WHEN
                    ${TutorProfileQ.approved_at} IS NULL
                    THEN 1
                    ELSE 2
                END
                ${UserQ.getTutorProfile()}
            )
        )
    `,

  getCertificationProfile: (): SQLFragment<CertificationProfileAttributes> => SQL`
        ${CertificationProfileQ.where({
          userId: UserQ.id,
        })}
        ORDER BY ${CertificationProfileQ.created_at} ASC
        LIMIT 1
    `,

  getCertificationProfileStatus: (): SQLFragment<CertificationStatus> => SQL`
        GREATEST(
            0,
            (SELECT
                CASE WHEN
                    ${CertificationProfileQ.created_at} IS NULL
                    THEN 1
                    ELSE 2
                END
                ${UserQ.getCertificationProfile()}
            )
        )
    `,

  getGreatestCertificationRequestStatus: (): SQLFragment<CertificationStatus> => SQL`
        GREATEST(
            0,
            (SELECT
                CASE WHEN
                    ${CertificationRequestQ.approved_at} IS NULL
                    THEN 1
                    ELSE 2
                END
                ${CertificationRequestQ.where({
                  userId: UserQ.id,
                })}
                ORDER BY ${CertificationRequestQ.approved_at} ASC
                LIMIT 1
            )
        )
    `,

  countApprovedCertifications: (): SQLFragment<number> =>
    SQL`count(*)::integer ${CertificationRequestQ.where({ userId: UserQ.id, reviewed: 'approved' })} `,

  getSessionRegistrations: ({
    earliestDate,
    latestDate,
    includeSession = false,
    attemptedJoining = null,
    paranoid = true,
  }: {
    earliestDate?: SQLFragment<Date>
    latestDate?: SQLFragment<Date>
    includeSession?: boolean
    attemptedJoining?: boolean | null
    paranoid?: boolean
  }): SQLFragment<SessionRegistrationAttributes & SessionAttributes> => SQL`
            ${SessionRegistrationQ.where(
              { userId: UserQ.id, includeSession, paranoid },
              SQL`${
                earliestDate != null
                  ? SQL`${SessionQ.getComputedEndDate()} > ${earliestDate}`
                  : SQL`TRUE`
              } AND
                ${
                  latestDate != null
                    ? SQL`${SessionQ.getComputedEndDate()} < ${latestDate}`
                    : SQL`TRUE`
                } AND
                ${
                  attemptedJoining != null
                    ? SQL`${SessionRegistrationQ.attempted_joining} = ${attemptedJoining}`
                    : SQL`TRUE`
                }`
            )}
        `,

  getAttendedMinutes: (): SQLFragment<number> => SQL`
    SUM(${SessionAttendeeQ.duration_seconds}) / 60
    ${SessionAttendeeQ.where({
      userId: UserQ.id,
      include: [{ association: 'Session', required: true }],
    })}
  `,

  // We query VolunteerActivity as a backup because the rows persist after sessions get deleted (as opposed to SessionHost).
  // We use the maximum value between these two—neither is a perfect source of truth.
  getTutoringMinutesWithStatsCorrection: (): SQLFragment<number> => SQL`
    GREATEST(
      (SELECT ${UserQ.getTutoringMinutes({})}),
      (SELECT ${VolunteerActivityQ.getTutoringMinutes()})
    )
  `,

  getTutoringMinutes: ({ start, end }: { start?: Date; end?: Date }): SQLFragment<number> => SQL`
      SUM(${SessionHostQ.tutoring_seconds})::NUMERIC / 60
      ${SessionHostQ.where(
        {
          include: [{ association: 'Session', required: true }],
          userId: UserQ.id,
        },
        SQL`
          ${start != null ? SQL`${SessionQ.start_date} >= ${start.toISOString()}` : SQL`TRUE`} AND
          ${end != null ? SQL`${SessionQ.end_date} <= ${end.toISOString()}` : SQL`TRUE`}
        `
      )}
    `,

  getLearningMinutes: ({ start, end }: { start?: Date; end?: Date }): SQLFragment<number> => SQL`
    SUM(${SessionAttendeeQ.duration_seconds})::NUMERIC / 60
    ${SessionAttendeeQ.where(
      {
        roles: [SessionAttendeeRole.Learner],
        include: [{ association: 'Session', required: true }],
        userId: UserQ.id,
      },
      SQL`
        ${start != null ? SQL`${SessionQ.start_date} >= ${start.toISOString()}` : SQL`TRUE`} AND
        ${end != null ? SQL`${SessionQ.end_date} <= ${end.toISOString()}` : SQL`TRUE`}
      `
    )}
  `,

  // Filters live help sessions
  getParticipatingSessions: ({
    userId,
    worldId = null,
    past = null,
    customWhere = SQL`TRUE`,
    includeAttendingLiveHelp = false,
    includeUnregisteredSeriesSessions = false,
    includeReservations = false,
    include = [
      { association: 'Topic', required: true },
      { association: 'Series', required: false },
    ],
  }: {
    userId: number | SQLFragment<number>
    worldId?: number | SQLFragment<number> | null
    past?: boolean | null
    customWhere?: SQLFragment<unknown>
    includeAttendingLiveHelp?: boolean
    includeReservations?: boolean
    // Whether or not to include sessions that are a part of a series user unregistered from.
    includeUnregisteredSeriesSessions?: boolean
    include?: IncludeAssociations
  }): SQLFragment<SessionAttributes> => SQL`
        ${SessionQ.where(
          {
            worldId,
            include,
          },
          SQL`
                (
                    ${SessionQ.hasUserRegistered(userId)}
                    OR ${SessionQ.isHost({ userId, approved: true })}
                    OR ${
                      includeReservations ? SessionQ.hasUserSelfReservedSeries(userId) : SQL`FALSE`
                    }
                )
                AND (
                  ${
                    includeUnregisteredSeriesSessions
                      ? SQL`TRUE`
                      : SQL`
                        ${SeriesQ.id} IS NULL
                      OR (SELECT ${SeriesQ.isUserRegistered(userId)})
                      OR (SELECT ${SeriesQ.isHost({ userId })})
                      OR (Select ${SessionQ.isUserDropIn(userId)})
                      OR ${includeReservations ? SeriesQ.isUserSelfReserved(userId) : SQL`FALSE`}`
                  }
                )
                AND ${
                  past == null
                    ? SQL`TRUE`
                    : past
                      ? SQL`${SessionQ.getComputedEndDate()} < NOW()`
                      : SQL`${SessionQ.getComputedEndDate()} > NOW()`
                }
                ${
                  includeAttendingLiveHelp
                    ? SQL``
                    : SQL`AND ${filterAttendingLiveHelpSessions(userId)}`
                }
                AND ${customWhere}
            `
        )}
    `,

  getParticipatingSeries: ({
    userId,
    past = false,
    includeReservations = false,
    customWhere = SQL`TRUE`,
    include = [],
  }: {
    userId: number | SQLFragment<number>
    past?: boolean
    includeReservations?: boolean
    customWhere?: SQLFragment<unknown>
    include?: IncludeAssociations
  }): SQLFragment<SeriesAttributes> => SQL`
    ${SeriesQ.where(
      {
        includeAllResearchGroups: true,
        include: [{ association: 'Course', required: true }, ...include],
      },
      SQL`
        ${past ? SQL`NOT ` : SQL``} ${SeriesQ.hasUpcomingSession()}
        AND
        (
            ${SeriesQ.id} IN (
                SELECT ${SeriesRegistrationQ.series_id}
                ${SeriesRegistrationQ.where({
                  userId,
                  include: [
                    {
                      association: 'Series',
                      required: true,
                    },
                  ],
                })}
               ${
                 includeReservations
                   ? SQL`
               UNION
                SELECT ${SeriesReservationQ.series_id}
                  ${SeriesReservationQ.where(
                    {
                      userId,
                      reserverUserId: userId,
                      include: [
                        {
                          association: 'Series',
                          required: true,
                        },
                      ],
                    },
                    SeriesReservationQ.isActive()
                  )}
               `
                   : SQL``
               }
            )
            OR
            ${SeriesQ.isHost({ userId, approved: true })}
        )
        AND ${customWhere}
        `
    )}
    `,
  getSeriesAsMainHost: ({
    userId,
    customWhere = null,
  }: {
    userId: number | SQLFragment<number>
    customWhere?: SQLFragment<unknown> | null
  }): SQLFragment<SeriesAttributes> =>
    SeriesQ.where({ userId }, SQL`${customWhere != null ? customWhere : SQL`TRUE`}`),

  countFeaturedSessionFeedback: (): SQLFragment<number> => SQL`
    COUNT(*) ${SessionFeedbackFeaturedQ.where({ userId: UserQ.id })}
    `,

  countFeaturedLearnerFeedback: ({
    feedbackType,
    isSelfReflection,
  }: {
    feedbackType?: LearnerFeedbackType
    isSelfReflection?: boolean
  }): SQLFragment<number> => SQL`
    COUNT(*) ${LearnerFeedbackFeaturedQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'LearnerFeedback',
          required: true,
          where: SQL`${
            feedbackType != null ? LearnerFeedbackQ.isLearnerFeedbackType(feedbackType) : SQL`TRUE`
          } AND ${LearnerFeedbackQ.selfReflectionFilter(isSelfReflection)}`,
        },
      ],
    })}
    `,

  countFeaturedAudit: (): SQLFragment<number> => SQL`
    COUNT(*) ${TutorAuditFeaturedQ.where({ userId: UserQ.id })}
    `,

  countTotalAudits: (): SQLFragment<number> => SQL`
    COUNT(*) ${TutorAuditQ.where({ auditorId: UserQ.id })}
  `,

  countWeekAudits: (): SQLFragment<number> => SQL`
    COUNT(*) ${TutorAuditQ.where(
      { auditorId: UserQ.id },
      SQL`${TutorAuditQ.created_at} > NOW() - INTERVAL '1 WEEK'`
    )}
  `,

  countMonthAudits: (): SQLFragment<number> => SQL`
    COUNT(*) ${TutorAuditQ.where(
      { auditorId: UserQ.id },
      SQL`${TutorAuditQ.created_at} > NOW() - INTERVAL '1 MONTH'`
    )}
  `,

  getMostRecentAudit: (): SQLFragment<TutorAuditAttributes> => SQL`
      ${TutorAuditQ.where({
        auditorId: UserQ.id,
        order: [[TutorAuditQ.created_at, 'desc']],
      })}
      LIMIT 1
  `,

  hasTutorAuditorLevel: ({
    auditorLevel,
  }: {
    auditorLevel: TutorAuditorLevel
  }): SQLFragment<boolean> =>
    SQL`(SELECT ${TutorProfileQ.auditor_level} ${TutorProfileQ.where({
      userId: UserQ.id,
    })}) = ${auditorLevel}
  `,

  getTutoredSessionCourses: (): SQLFragment<CourseAttributes> =>
    SessionHostQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'Session',
          required: true,
          where: SQL`${SessionQ.attempted_hosting}`,
          include: [
            {
              association: 'World',
              required: true,
              include: [
                {
                  association: 'Course',
                  required: true,
                },
              ],
            },
          ],
        },
      ],
    }),

  getTutoredSeriesCourses: (): SQLFragment<CourseAttributes> =>
    SessionHostQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'Session',
          required: true,
          where: SQL`${SessionQ.attempted_hosting} AND ${SessionQ.series_id} IS NOT NULL`,
          include: [
            {
              association: 'World',
              required: true,
              include: [
                {
                  association: 'Course',
                  required: true,
                },
              ],
            },
          ],
        },
      ],
    }),

  getRecommendedTopics: (): SQLFragment<TopicAttributes> => SQL`
            ${TopicQ.where(
              {},
              SQL`
                (
                    ${TopicQ.id} IN (
                        SELECT ${TopicFollowQ.topic_id} ${TopicFollowQ.where({ userId: UserQ.id })}
                    ) OR ${TopicQ.id} IN (
                        SELECT DISTINCT t1.id FROM ${TopicQ} t1 INNER JOIN ${TopicQ} t2
                        ON t1.course_id = t2.course_id
                        AND t1.type = ${TopicType.qa}
                        AND t2.id IN (
                            SELECT ${TopicFollowQ.topic_id}
                            ${TopicFollowQ.where({ userId: UserQ.id })}
                        )
                    ) OR ${TopicQ.id} IN (
                        SELECT ${SessionQ.topic_id}
                        ${SessionQ.where(
                          {},
                          SQL`
                                ${SessionQ.id} IN (
                                    SELECT ${SessionRegistrationQ.session_id}
                                    ${SessionRegistrationQ.where({ userId: UserQ.id })}
                                )
                            `
                        )}
                    )
                )
                `
            )}
    `,

  getRecommendedSessions: ({
    order = [[SessionQ.start_date, 'asc']],
    limit = null,
    type = 'session',
    includeLiveHelp = false,
  }: {
    order?: QueryOrder | null
    limit?: number | null
    type?: 'session' | 'series'
    includeLiveHelp?: boolean
  }): SQLFragment<SessionAttributes> => SQL`
        ${SessionHostQ.where(
          {
            include: [
              {
                association: 'Session',
                required: true,
                include: [
                  {
                    association: 'Topic',
                    required: true,
                    include: [
                      {
                        association: 'Course',
                        required: true,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          SQL`
                ${SessionQ.series_id} ${type === 'series' ? SQL`IS NOT NULL` : SQL`IS NULL`}
                AND ${SessionQ.getComputedEndDate()} > NOW()
                AND ${SessionQ.userCanViewSession(UserQ.id)}
                AND ${UserQ.id} NOT IN (
                    SELECT ${SessionRegistrationQ.user_id}
                    ${SessionRegistrationQ.where({ sessionId: SessionQ.id })})
                AND NOT ${SessionQ.isHost({ userId: UserQ.id, approved: true })}
                AND
                (
                    (${SessionQ.topic_id} IN (SELECT ${TopicQ.id} ${UserQ.getRecommendedTopics()}))
                    OR
                    (
                        ${SessionHostQ.user_id} IN (
                            SELECT ${TutorFollowQ.tutor_id} ${TutorFollowQ.where({
                              userId: UserQ.id,
                            })}
                        )
                    )
                )
                ${includeLiveHelp ? SQL`` : SQL`AND ${TopicQ.name} != ${LIVE_HELP_TOPIC}`}
            `
        )}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  getZipCodeInfo: (): SQLFragment<ZipCodeInfoAttributes> =>
    ZipCodeInfoQ.where({
      zipCode: SQL`${UserQ.onboarding_details}->>'zipcode'`,
    }),
  // TODO: The current implementation re-uses the getRecommendedSessions query. If these queries
  // are called from the same place, we will be making a duplicate query. To improve performance,
  // we could combine them into a single query and return both results using WITH.
  getRecommendedSeries: ({
    order = [
      [SQL`(SELECT ${SeriesQ.countCompletedSessions()})`, 'asc'],
      [SQL`(SELECT ${SessionQ.start_date} ${SeriesQ.getNextSession()})`, 'asc'],
    ],
    limit = null,
  }: {
    order?: QueryOrder | null
    limit?: number | null
  }): SQLFragment<SeriesAttributes> => SQL`
        ${SeriesQ.where(
          { include: [{ association: 'Course', required: true }] },
          SQL`
                (SELECT ${SeriesQ.getComputedEndDate()}) > NOW()
                AND ${SeriesQ.userCanViewSeries(UserQ.id)}
                AND ${UserQ.id} NOT IN (
                    SELECT ${SeriesRegistrationQ.user_id}
                    ${SeriesRegistrationQ.where({ seriesId: SeriesQ.id })}
                )
                AND NOT ${SeriesQ.isHost({ userId: UserQ.id })}
                AND (
                    ${SeriesQ.id} IN (
                        SELECT ${SessionQ.series_id}
                        ${UserQ.getRecommendedSessions({
                          type: 'series',
                        })}
                    )
                )
		`
        )}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
	`,

  getFeaturedCertifiedTutors: (): SQLFragment<UserAttributes[]> =>
    SQL`${UserQ.where({})} AND ${UserQ.id} IN (
            SELECT ${WebsiteSettingsQ.getFeaturedCertifiedTutors()}
        )`,

  getSessionMentorActions: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<(SessionMentorAction & { session_id: SessionAttributes['id'] })[]> => SQL`
      jsonb_agg(
        x
      ) FROM (
        SELECT
        x.*, ${SessionQ.id} as "session_id" FROM ${SessionQ}, jsonb_to_recordset(${
          SessionQ.mentor_actions
        })
          as x("timestamp" timestamptz, "status" varchar, "userId" int, "userName" varchar, "comment" varchar)
        WHERE ${SessionQ.deleted_at} IS NULL AND ${SessionQ.id} IN (
          SELECT ${SessionQ.id}
          ${UserQ.getTutoringSessions({ userId, requireLearnerAttendance: false })}
      )
        ORDER BY timestamp desc
      ) x
  `,

  countPointsSince: ({ fromDate }: { fromDate: SQLFragment<unknown> }): SQLFragment<number> => SQL`(
    SELECT SUM(${UserEventQ.event_points})
      ${UserEventQ.where({ userId: UserQ.id }, SQL`${UserEventQ.created_at} > ${fromDate}`)})`,

  getJoinReason: (): SQLFragment<JoinReason> => SQL`COALESCE(
    ${UserQ.onboarding_details}->>${EDUCATION_K_12_JOIN_REASON_INPUT_NAME},
    ${UserQ.onboarding_details}->>${EDUCATION_NOT_K_12_JOIN_REASON_INPUT_NAME}
  )`,

  intendsToTutor: (): SQLFragment<boolean> => SQL`(
       ${UserQ.getJoinReason()} IS NOT NULL AND (
       ${UserQ.getJoinReason()} = ${JoinReason.Tutor} OR
       ${UserQ.getJoinReason()} = ${JoinReason.Tutoring}
    )
  )`,

  getEducationType: (): SQLFragment<EducationInputValue> =>
    SQL`${UserQ.onboarding_details}->>${EDUCATION_INPUT_NAME}`,

  getSchoolExperience: (): SQLFragment<SchoolExperience> => SQL`COALESCE(
    ${UserQ.onboarding_details}->>${EDUCATION_K_12_SCHOOL_EXPERIENCE_INPUT_NAME}
  )`,

  getCountriesMet: ({
    dateFilterMin,
    dateFilterMax,
  }: {
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<number> => SQL`FROM (SELECT country, COUNT(*)
      ${UserQ.distinctPeersMet({ dateFilterMin, dateFilterMax })}
        WHERE country IS NOT NULL
        GROUP BY country
        ORDER BY count DESC
      ) as countries
    `,

  distinctPeersMet: ({
    dateFilterMin,
    dateFilterMax,
  }: {
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<unknown> => SQL`
    FROM (
      SELECT DISTINCT ON (u2.id) u2.*
      FROM ${SessionAttendeeQ}
      LEFT JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionAttendeeQ.session_id}
      LEFT JOIN ${UserQ} u2 ON u2.id = ${SessionAttendeeQ.user_id}
      WHERE ${SessionQ.id} IN (
        SELECT s2.session_id from ${SessionAttendeeQ} s2
          WHERE s2.user_id = ${UserQ.id}
      )
      AND ${
        dateFilterMin == null ? SQL`TRUE` : SQL`${SessionAttendeeQ.joined_at} > ${dateFilterMin}`
      }
      AND ${
        dateFilterMax == null ? SQL`TRUE` : SQL`${SessionAttendeeQ.joined_at} < ${dateFilterMax}`
      }
    ) as res
  `,

  distinctLearnersImpacted: ({
    dateFilterMin,
    dateFilterMax,
  }: {
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<unknown> => SQL`
    FROM (
      SELECT DISTINCT ON (u2.id) u2.*
      FROM ${SessionAttendeeQ}
      LEFT JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionAttendeeQ.session_id}
      LEFT JOIN ${UserQ} u2 ON u2.id = ${SessionAttendeeQ.user_id}
      WHERE ${SessionQ.id} IN (
        SELECT ${SessionQ.id}
        ${UserQ.getTutoringSessions({ userId: UserQ.id, requireLearnerAttendance: false })}
      )
      AND ${SessionAttendeeQ.role} != ${SessionAttendeeRole.Tutor}
      AND ${
        dateFilterMin == null ? SQL`TRUE` : SQL`${SessionAttendeeQ.joined_at} > ${dateFilterMin}`
      }
      AND ${
        dateFilterMax == null ? SQL`TRUE` : SQL`${SessionAttendeeQ.joined_at} < ${dateFilterMax}`
      }
    ) as res
  `,

  getCountriesImpacted: ({
    dateFilterMin,
    dateFilterMax,
  }: {
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<number> => SQL`FROM (SELECT country, COUNT(*)
      ${UserQ.distinctLearnersImpacted({ dateFilterMin, dateFilterMax })}
        WHERE country IS NOT NULL
        GROUP BY country
        ORDER BY count DESC
      ) as countries
    `,

  countLearnersImpactedForStatistics: ({
    dateFilterMin,
    dateFilterMax,
  }: {
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<number> =>
    SQL`((SELECT COUNT(id) ${UserQ.distinctLearnersImpacted({
      dateFilterMin,
      dateFilterMax,
    })}) + (SELECT COUNT(${UserEventQ.id}) ${UserEventQ.where({ userId: UserQ.id, eventType: 'learner_account_deleted' })}))`,

  countCountriesImpactedForStatistics: ({
    dateFilterMin = null,
    dateFilterMax,
  }: {
    dateFilterMin?: SQLFragment<unknown> | null
    dateFilterMax?: SQLFragment<unknown> | null
  }): SQLFragment<number> => SQL`COUNT(DISTINCT country) FROM (
    SELECT country ${UserQ.distinctLearnersImpacted({
      dateFilterMin,
      dateFilterMax,
    })}
    UNION
    SELECT ${UserEventQ.event_payload}->>'country' ${UserEventQ.where({ userId: UserQ.id, eventType: 'learner_account_deleted' })}
  )`,

  isSATBootcampTutor: (): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${SATUserQ.where({ userId: UserQ.id, type: 'tutor' })})
  `,

  needsNewTutorOnboardingSession: ({
    excludeAttendedSessionId,
  }: {
    excludeAttendedSessionId?: number
  }): SQLFragment<boolean> => SQL`
    (${UserQ.hasTutorRole()}) AND
    (SELECT ${UserQ.countPastHostedSessions({
      requireLearnerAttendance: false,
    })}) < ${SUGGEST_NEW_TUTOR_ONBOARDING_UNTIL_NUM_SESSIONS_HOSTED} AND
    (NOT EXISTS(SELECT 1 ${SessionAttendeeQ.where(
      {
        userId: UserQ.id,
        include: [
          {
            association: 'Session',
            required: true,
            include: [
              {
                association: 'Topic',
                required: true,
                where: SQL`${TopicQ.name} = ${NEW_TUTOR_ONBOARDING_TOPIC}`,
              },
            ],
          },
        ],
        roles: [SessionAttendeeRole.Learner],
      },
      excludeAttendedSessionId != null
        ? SQL`${SessionAttendeeQ.session_id} != ${excludeAttendedSessionId}`
        : null
    )}))
   `,

  submittedAssessment: (assessmentId: number): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${AssessmentSubmissionQ.where({ userId: UserQ.id, assessmentId })})
  `,

  hasHostedEnoughSessionsToAudit: (userId: number): SQLFragment<boolean> => SQL`(SELECT
    COUNT(DISTINCT ${SessionAttendeeQ.session_id}) >= ${AUDITOR_SESSIONS_HOSTED_MIN_THRESHOLD}
    ${SessionAttendeeQ.where({
      userId,
      roles: [SessionAttendeeRole.Tutor],
    })}
  )`,

  justHostedEnoughSessionsToAudit: (userId: number): SQLFragment<boolean> => SQL`(SELECT
    COUNT(DISTINCT ${SessionAttendeeQ.session_id}) = ${AUDITOR_SESSIONS_HOSTED_MIN_THRESHOLD}
    ${SessionAttendeeQ.where({
      userId,
      roles: [SessionAttendeeRole.Tutor],
    })}
  )`,

  attendedSessionWithTopic: ({
    userId,
    topicName,
    roles = [SessionAttendeeRole.Learner, SessionAttendeeRole.OnboardingBuddy],
  }: {
    userId: number | SQLFragment<number>
    topicName: string
    roles?: SessionAttendeeRole[]
  }): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SessionAttendeeQ.where({
      userId,
      roles,
      include: [
        {
          association: 'Session',
          required: true,
          include: [
            {
              association: 'Topic',
              required: true,
              where: SQL`${TopicQ.name} = ${topicName}`,
            },
          ],
        },
      ],
    })})`,

  getResearchGroup: (): SQLFragment<ResearchGroup | null> =>
    SQL`${UserQ.internal_state}->>'research_group'`,

  getMigratedSlackJoinDate: (): SQLFragment<string> =>
    SQL`${UserQ.internal_state}->>'migrated_slack_join_date'`,

  getMigratedDialoguesData: (): SQLFragment<MigratedDialoguesData | null> => SQL`
    (${UserQ.internal_state})->'migrated_dialogues_data'
  `,

  getTutorSinceDate: (): SQLFragment<string> => SQL`
    CASE WHEN ${UserQ.getMigratedSlackJoinDate()} IS NOT NULL THEN ${UserQ.getMigratedSlackJoinDate()}
    ELSE
      (SELECT to_char (${SessionQ.start_date}::timestamp at time zone 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS"Z"')
      ${SessionQ.where({
        tutor: UserQ.id,
        order: [[SessionQ.start_date, 'asc']],
      })} LIMIT 1)
    END
  `,

  isVerified: (): SQLFragment<boolean> => SQL`is_verified(${UserQ})`,

  hasJoinedSlack: (): SQLFragment<boolean> =>
    SQL`(
      SELECT ${TutorProfileQ.getSlackEmail()} IS NOT NULL
      ${TutorProfileQ.where({ userId: UserQ.id })}
    )`,

  isRegisteredForSATBootcamp: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesRegistrationQ.where({
      userId,
      include: [
        {
          association: 'Series',
          required: true,
          include: [
            {
              association: 'SATSeries',
              required: true,
            },
          ],
        },
      ],
    })})`,

  hasCompletedTraining: ({
    slug,
    userId,
  }: {
    slug: string
    userId: number | SQLFragment<number>
  }): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT 1
      ${TrainingSubmissionQ.where({
        userId,
        hasSubmitted: true,
        include: [
          {
            association: 'Training',
            required: true,
            where: SQL`${TrainingQ.slug} = ${slug}`,
          },
        ],
      })}
    )
  `,

  hasReviewedInWorld: ({ slug }: { slug: string }): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT 1 FROM ${TutorAuditQ}, ${SessionQ}, ${WorldQ}
        WHERE ${TutorAuditQ.auditor_id} = ${UserQ.id}
          AND ${SessionQ.id} = ${TutorAuditQ.session_id}
          AND ${WorldQ.id} = ${SessionQ.world_id}
          AND ${WorldQ.slug} = ${slug}
    )
  `,

  hasPeerReviewed: (): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${TutorAuditQ.where({ auditorId: UserQ.id })})
  `,

  hasCompletedPeerReviewTraining: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<boolean> => SQL`(
    (${UserQ.hasCompletedTraining({ slug: PEER_REVIEW_TRAINING_SLUG, userId })}) OR
    (${UserQ.hasCompletedTraining({ slug: OLD_AUDITOR_TRAINING_SLUG, userId })})
  )`,

  getSessionFeedbacksFeatured: (): SQLFragment<SessionFeedbackFeaturedAttributes> =>
    SessionFeedbackFeaturedQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'SessionFeedback',
          required: true,
        },
      ],
    }),

  getTutorAuditsFeatured: (): SQLFragment<TutorAuditFeaturedAttributes> =>
    TutorAuditFeaturedQ.where({
      userId: UserQ.id,
      includeTutorAudit: true,
    }),

  getPostFeedbacksFeatured: (): SQLFragment<PostFeedbackFeaturedAttributes> =>
    PostFeedbackFeaturedQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'PostFeedback',
          required: true,
        },
      ],
    }),

  getLearnerFeedbacksFeatured: ({
    feedbackType,
    isSelfReflection,
  }: {
    feedbackType?: LearnerFeedbackType
    isSelfReflection?: boolean
  }): SQLFragment<LearnerFeedbackFeaturedAttributes[]> =>
    LearnerFeedbackFeaturedQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'LearnerFeedback',
          required: true,
          where: SQL`${
            feedbackType != null ? LearnerFeedbackQ.isLearnerFeedbackType(feedbackType) : SQL`TRUE`
          } AND ${LearnerFeedbackQ.selfReflectionFilter(isSelfReflection)}`,
        },
      ],
    }),

  getLearnerFeedbacks: ({
    feedbackType,
    isSelfReflection,
  }: {
    feedbackType: LearnerFeedbackType
    isSelfReflection?: boolean
  }): SQLFragment<LearnerFeedbackAttributes[]> =>
    LearnerFeedbackQ.where(
      {
        revieweeUserId: UserQ.id,
        type: feedbackType,
      },
      SQL`${LearnerFeedbackQ.selfReflectionFilter(isSelfReflection)}`
    ),

  getPastVolunteerRoles: (): SQLFragment<UserVolunteerRoleAttributes> =>
    UserVolunteerRoleQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'VolunteerRole',
          required: true,
        },
      ],
    }),
  getActiveVolunteerTeams: ({ userId }: { userId: number }): SQLFragment<VolunteerTeamAttributes> =>
    VolunteerTeamQ.where({
      include: [
        {
          association: 'VolunteerRoles',
          required: true,
          include: [
            {
              association: 'UserVolunteerRoles',
              required: true,
              where: SQL`
              ${UserVolunteerRoleQ.user_id} = ${userId}
              AND ${UserVolunteerRoleQ.isActive()}
            `,
            },
          ],
        },
      ],
    }),
  canManageVolunteerTeam: ({ slug }: { slug: string }): SQLFragment<boolean> =>
    SQL`
    (
      (SELECT ${UserQ.hasSuperAdminRole()})
      OR EXISTS(
        SELECT 1
        ${UserVolunteerRoleQ.where({
          userId: UserQ.id,
          isActive: true,
          include: [
            {
              association: 'VolunteerRole',
              required: true,
              include: [
                {
                  association: 'VolunteerTeam',
                  required: true,
                  where: SQL`
                    ${VolunteerTeamQ.slug} = ${slug}
                    AND ${VolunteerRoleQ.type} = 'lead'`,
                },
              ],
            },
          ],
        })}
      )
    )`,

  canManageVolunteerRole: ({
    volunteerRoleId,
  }: {
    volunteerRoleId: number | SQLFragment<number>
  }): SQLFragment<boolean> => SQL`
    (
      (SELECT ${UserQ.hasSuperAdminRole()})
      OR EXISTS(
        SELECT 1
        FROM ${UserVolunteerRoleQ}
        JOIN ${VolunteerRoleQ} source ON ${UserVolunteerRoleQ.volunteer_role_id} = source.id
        JOIN ${VolunteerTeamQ} ON source.volunteer_team_id = ${VolunteerTeamQ.id}
        JOIN ${VolunteerRoleQ} dest ON ${VolunteerTeamQ.id} = dest.volunteer_team_id
        WHERE (
          source.type = 'lead'
          AND dest.id = ${volunteerRoleId}
          AND dest.type IN ('member', 'lead')
          AND ${UserVolunteerRoleQ.user_id} = ${UserQ.id}
          AND ${UserVolunteerRoleQ.isActive()}
          AND ${UserVolunteerRoleQ.deleted_at} IS NULL
          AND source.deleted_at IS NULL
          AND dest.deleted_at IS NULL
          AND ${VolunteerTeamQ.deleted_at} IS NULL
        )
      )
    )`,

  hasHostedHighlyEngagingSATBootcamp: (
    userId: number | SQLFragment<number>,
    customWhere?: SQLFragment<unknown>
  ): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${SeriesTagQ.getHighlyEngagingBootcampsForUser(userId, customWhere)})`,

  countAcademicSessionsHosted: (userId: number | SQLFragment<number>): SQLFragment<number> =>
    SQL`
      COUNT(*) ${SessionQ.getAcademicSessionsHostedBy(userId)}
    `,

  countMinutesSpokenInAcademicSessions: (
    userId: number | SQLFragment<number>
  ): SQLFragment<number> =>
    SQL`
        (SUM(${SessionAttendeeQ.speaking_duration_seconds})::float / 60)
        ${SessionAttendeeQ.where({
          userId,
          include: [
            {
              association: 'Session',
              required: true,
              include: [
                {
                  association: 'World',
                  required: true,
                  where: WorldQ.isAcademic(),
                },
              ],
            },
          ],
        })}
      `,

  countUniqueTutorsRated: (userId: number | SQLFragment<number>): SQLFragment<number> =>
    SQL`
      COUNT(DISTINCT ${SessionQ.user_id})
      ${SessionFeedbackQ.where({
        userId,
        type: SESSION_FEEDBACK_TYPE_TUTOR,
        include: [
          {
            association: 'Session',
            required: true,
          },
        ],
      })}
    `,

  countUniqueLearnersWithPositiveRating: (
    userId: number | SQLFragment<number>
  ): SQLFragment<number> =>
    SQL`
      COUNT(DISTINCT ${SessionFeedbackQ.user_id})
      FROM
        ${SessionFeedbackQ}
        JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionFeedbackQ.session_id}
        JOIN ${SessionHostQ} ON
          ${SessionHostQ.session_id} = ${SessionQ.id}
          AND ${SessionHostQ.user_id} = ${userId}
        JOIN ${SessionAttendeeQ} ON
          ${SessionAttendeeQ.session_id} = ${SessionQ.id}
          AND ${SessionAttendeeQ.user_id} = ${userId}
      WHERE
        ${SessionFeedbackQ.rating} > ${TutorSessionFeedbackRating.NotHelpful}
        AND ${SessionFeedbackQ.type} = ${SESSION_FEEDBACK_TYPE_TUTOR}
        AND ${SessionQ.deleted_at} IS NULL
        AND ${SessionHostQ.deleted_at} IS NULL
        AND ${SessionAttendeeQ.deleted_at} IS NULL
    `,

  countCertifications: (userId: number | SQLFragment<number>): SQLFragment<number> =>
    SQL`
      COUNT(DISTINCT ${CertificationRequestQ.topic_id})
      ${CertificationRequestQ.where({
        userId,
        reviewed: 'approved',
      })}
    `,

  countContentFlags: (
    userId: number | SQLFragment<number>,
    severity: ModerationFlagSeverity
  ): SQLFragment<number> =>
    SQL`
      COUNT(*)
      FROM
        ${ContentFlagQ} AS content_flag1
      WHERE
        content_flag1.user_id = ${userId}
        AND content_flag1.severity = ${severity}
        AND content_flag1.deleted_at IS NULL
    `,

  countDetectedIssues: (
    userId: number | SQLFragment<number>,
    urgencies: DetectedIssueUrgency[] = ['low', 'medium', 'high']
  ): SQLFragment<number> => SQL`
    SUM(CASE WHEN detected_issue1.type = ANY (${Object.keys(DETECTED_ISSUE_TYPE_TO_URGENCY).filter(
      type => urgencies.includes(DETECTED_ISSUE_TYPE_TO_URGENCY[type as DetectedIssueType])
    )}) THEN 1 ELSE 0 END)
    FROM
      ${DetectedIssueQ} AS detected_issue1
    WHERE
      detected_issue1.user_id = ${userId}
      AND detected_issue1.strike_voided_at IS NULL
      AND detected_issue1.deleted_at IS NULL
  `,

  countAcademicSessionsWithNoTalkOrChat: (
    userId: number | SQLFragment<number>
  ): SQLFragment<number> =>
    SQL`
      COUNT(*)
      FROM
        (
          SELECT
            ${SessionQ.id},
            SUM(${SessionAttendeeQ.speaking_duration_seconds}) AS seconds_talking,
            SUM(${SessionAttendeeQ.count_chats}) as chat_count
          FROM
            ${SessionAttendeeQ}
            JOIN ${SessionQ} ON ${SessionQ.id} = ${SessionAttendeeQ.session_id}
            JOIN ${WorldQ} ON ${WorldQ.id} = ${SessionQ.world_id}
          WHERE
            ${SessionAttendeeQ.user_id} = ${userId}
            AND ${SessionAttendeeQ.session_breakout_id} IS NULL
            AND ${SessionAttendeeQ.hasRole(SessionAttendeeRole.Learner)}
            AND ${WorldQ.isAcademic()}

            AND ${SessionAttendeeQ.deleted_at} IS NULL
            AND ${SessionQ.deleted_at} IS NULL

          GROUP BY ${SessionQ.id}
        ) AS participation
      WHERE
        participation.seconds_talking = 0
        AND participation.chat_count = 0
    `,

  // N.B: Returns a Postgres interval that resolves to a string
  getAge(): SQLFragment<string> {
    return SQL`(
      CASE WHEN ${UserQ.date_of_birth} IS NULL THEN NULL
      ELSE AGE(${UserQ.date_of_birth})
      END
    )`
  },

  // Skip background checks for SAT Bootcamp and CAW subworlds
  // Skip background checks for users who are under 18
  // Skip background checks for users who are under 21 and are SAT Bootcamp tutors
  // Skip background checks for users who are under 24 and have verified college info and are CAW tutors
  canGetBackgroundCheck: ({ worldSlug }: { worldSlug?: string } = {}) => SQL<boolean>`(
    ${worldSlug == null || ![SAT_BOOTCAMP_WORLD_SLUG, COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG].includes(worldSlug) ? SQL`TRUE` : SQL`FALSE`}
    AND ${UserQ.getAge()} >= INTERVAL '18 years'
    AND NOT(
      ${UserQ.getAge()} < INTERVAL '21 years' AND EXISTS(
        SELECT 1 ${WorldQ.where({ slug: SAT_BOOTCAMP_WORLD_SLUG, include: [{ association: 'WorldMembers', required: true, where: SQL`${WorldMemberQ.user_id} = ${UserQ.id}` }] }, WorldMemberQ.isAtLeastLevel('tutor'))}
      )
    )
    AND NOT(
      ${UserQ.getAge()} < INTERVAL '24 years' AND ${UserQ.college_info_verified_at} IS NOT NULL AND EXISTS(
        SELECT 1 ${WorldQ.where({ slug: COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG, include: [{ association: 'WorldMembers', required: true, where: SQL`${WorldMemberQ.user_id} = ${UserQ.id}` }] }, WorldMemberQ.isAtLeastLevel('tutor'))}
      )
    )
  )`,

  countOffendingSafetyReports: (): SQLFragment<number> => SQL`
    COUNT(*) ${PlatformFeedbackQ.where({}, PlatformFeedbackQ.hasSubjectUser(UserQ.id))}
  `,

  isTargetLearner: (): SQLFragment<boolean | null> => SQL`
    (SELECT (${ZipCodeInfoQ.details}->>'median_income')::int < ${TARGET_USER_INCOME_THRESHOLD} ${UserQ.getZipCodeInfo()})
  `,

  // NOW() - date_of_birth doesn't compare nicely with 'year' intervals in Postgres and led to a bug. Intentionally using AGE.
  isOver18: (): SQLFragment<boolean> => SQL`${UserQ.getAge()} > INTERVAL '18' year`,

  isYoungerThan: (age: number): SQLFragment<boolean> =>
    SQL`${UserQ.getAge()} < ${age} * INTERVAL '1 year'`,

  isAgeBetween: (minAge: number, maxAge: number): SQLFragment<boolean> =>
    SQL`(EXTRACT(YEAR FROM ${UserQ.getAge()}) BETWEEN ${minAge} AND ${maxAge})`,

  isOver18OrBirthdayUnknown: (): SQLFragment<boolean> =>
    SQL`(${UserQ.date_of_birth} IS NULL OR ${UserQ.isOver18()})`,

  isInUnitedStates: (): SQLFragment<boolean> => SQL`${UserQ.country} = 'US'`,

  countMessages: (): SQLFragment<number> => SQL`
    COUNT(*) ${MessageQ.where({ userId: UserQ.id })}
  `,

  countOneOnOneRequests: (since: SQLFragment<Date> | null): SQLFragment<number> => SQL`
    COUNT(*) ${ConversationUserQ.where({
      userId: UserQ.id,
      include: [
        {
          association: 'Conversation',
          required: true,
          where: SQL`${ConversationQ.type} = 'one-on-one-request' AND ${since != null ? SQL`${ConversationQ.created_at} >= ${since}` : SQL`TRUE`}`,
        },
      ],
    })}
  `,

  isHeavilyRequestedOneOnOneTutor: (threshold = 5): SQLFragment<boolean> => SQL`
    (SELECT ${UserQ.countOneOnOneRequests(SQL`NOW() - INTERVAL '1 week'`)}) >= ${threshold}
  `,

  isFollowingTopic: ({
    topicId,
  }: {
    topicId: SQLFragment<number> | number
  }): SQLFragment<boolean> => SQL`
    EXISTS (
      SELECT 1
      ${TopicFollowQ.where({
        userId: UserQ.id,
        topicId,
      })}
    )`,

  hasHostedSeriesInWorld: (worldSlug: string): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT
        1
      ${SeriesHostQ.where({
        userId: UserQ.id,
        approved: true,
        include: [
          {
            association: 'Series',
            required: true,
            include: [
              {
                association: 'World',
                required: true,
                where: SQL`${WorldQ.slug} = ${worldSlug}`,
              },
            ],
          },
        ],
      })}
    )`,

  hasBeenOnlineInThePastNMonths: (months: number): SQLFragment<boolean> => SQL`
    ${UserQ.last_online_at} >= NOW() - (INTERVAL '1 MONTH' * ${months})
  `,

  isHostingOfferingForProgramIteration: (iteration: ProgramIteration): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT 1 ${SeriesHostQ.where({
        userId: UserQ.id,
        approved: true,
        include: [
          {
            association: 'Series',
            where: SeriesQ.isOfferingForProgramIteration(iteration),
          },
        ],
      })}
    )
  `,

  isMemberOfWorld: (subworldSlug: string): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT
        1
      ${WorldMemberQ.where({
        userId: UserQ.id,
        include: [
          {
            association: 'World',
            required: true,
            where: SQL`${WorldQ.slug} = ${subworldSlug}`,
          },
        ],
      })}
    )
  `,

  // Example usage:
  // Sessions that I can see: myUserId: me, targetUserId: tutorUserId
  // Users that I can tutor: myUserId: subjectUser, targetUserId: me
  isBucketCompatible: ({
    myUserId,
    targetUserId,
  }: {
    myUserId: number | SQLFragment<number>
    targetUserId: number | SQLFragment<number>
  }): SQLFragment<boolean> => SQL`is_bucket_compatible_between_users(${myUserId}, ${targetUserId})`,

  // Due to a previously confusing question in onboarding, we have a lot of tutor interest in CAW
  // We want to reduce the number of people we email from people who are not intending to tutor.
  // These two additional checks are to reduce the number of people we email, but we can get rid
  // of them after a couple of months pass (March 2025) and the funnel data is clean.
  isEligibleToTutorCAW: (): SQLFragment<boolean> => SQL`(
    ${UserQ.isOver18OrBirthdayUnknown()}
    AND ${UserQ.isInUnitedStates()}
  )`,

  isHostingRecentOrFutureSessions: ({
    lookaheadWindow,
  }: {
    lookaheadWindow?: Duration
  }): SQLFragment<boolean> => SQL`
    (
      SELECT
          COUNT(*)
      ${SessionHostQ.where(
        {
          userId: UserQ.id,
          approved: true,
          include: [
            {
              association: 'Session',
              required: true,
            },
          ],
        },
        SQL`
        (${SessionQ.getComputedEndDate()} >= NOW() - (${SESSION_END_DATE_BUFFER_DAYS} * INTERVAL '1 DAY')) AND
        ${lookaheadWindow != null ? SQL`${SessionQ.getComputedStartDate()} <= NOW() + (${lookaheadWindow.as('days')} * INTERVAL '1 DAY')` : SQL`TRUE`}
        `
      )}
    ) > 0`,

  hasSubmittedClosedRegistrationExperimentData: (): SQLFragment<boolean> =>
    SQL`${UserQ.internal_state}->>'sat_closed_registration_experiment_v2_data' IS NOT NULL`,

  // "Active" means accepted or pending
  hasActiveVolunteerRoleApplicationSubmission: (
    {
      slug,
    }: {
      slug: VolunteerRoleSlug
    },
    customWhere?: SQLFragment<boolean>
  ): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT 1 ${VolunteerRoleApplicationSubmissionQ.where(
        {
          userId: UserQ.id,
          hasBeenWithdrawn: false,
          hasBeenRejected: false,
          include: [
            {
              association: 'VolunteerRole',
              required: true,
              where: SQL`${VolunteerRoleQ.slug} = ${slug}`,
            },
          ],
        },
        customWhere
      )}
    )
  `,

  hasActiveVolunteerRole: ({ slug }: { slug: VolunteerRoleSlug }): SQLFragment<boolean> => SQL`
    EXISTS(
      SELECT 1 ${UserVolunteerRoleQ.where({
        userId: UserQ.id,
        isActive: true,
        include: [
          {
            association: 'VolunteerRole',
            required: true,
            where: SQL`${VolunteerRoleQ.slug} = ${slug}`,
          },
        ],
      })}
    )
  `,

  hasAttendedDialogues: ({
    userId,
  }: {
    userId: number | SQLFragment<number>
  }): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${UserQ.getAttendedSessions({
      userId,
      customWhere: SQL`${SessionQ.isDialoguesSeriesSession()}`,
    })})`,
}

export default UserQ
